import { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { manpowerApi } from '@/lib/apiClient';
import { fmtDateTime, toLocalDateInputValue } from '@/lib/dates';

/**
 * Company-wide crew-schedule audit stream (#350).
 *
 * Operations asked for this to "reduce the need for as much follow up on who did or
 * did not make the changes along with the associated timestamps" — so the job
 * of this view is answering that question by scanning, which is why it is a
 * full-width filterable list rather than a drawer beside the grid.
 *
 * ⚠️ SUMMARY GRAIN, deliberately. Every row here is an event that was already
 * being written to `activity_log`: a publish moved 9 crew days across a span, a
 * cell went 3 → 5. It does NOT show what every individual cell held before a
 * publish — per-cell before/after is the second table in #420 and a separate
 * build. Saying more than the data supports is the failure mode for an audit
 * view, so the empty and partial states below stay literal.
 */

// ─── Shared style constants (matched to ManpowerPage / LeadersView) ──────────

const TH: React.CSSProperties = {
  background: 'var(--ew-dark)',
  color: 'var(--ew-white)',
  fontWeight: 600,
  fontSize: 11,
  padding: '8px 10px',
  textAlign: 'left',
  whiteSpace: 'nowrap',
  borderRight: '1px solid rgba(255,255,255,0.07)',
};

const TD: React.CSSProperties = {
  borderBottom: '1px solid var(--ew-n-100)',
  borderRight: '1px solid var(--ew-n-100)',
  padding: '7px 10px',
  fontSize: 12,
  verticalAlign: 'top',
};

const SELECT: React.CSSProperties = {
  fontFamily: 'var(--ew-font)',
  fontSize: 12,
  padding: '5px 8px',
  border: '1px solid var(--ew-n-200)',
  borderRadius: 'var(--ew-r-md)',
  background: 'var(--ew-white)',
  color: 'var(--ew-n-600)',
  maxWidth: 260,
};

const PAGE_SIZE = 50;

/**
 * What each activity slug means in plain words.
 *
 * ⚠️ `crew_schedule_published` is snake_case unlike its neighbours and must
 * stay that way — the API matches the literal string. An unmapped slug falls
 * back to the raw value rather than rendering blank, so a new action added
 * server-side degrades to something readable instead of vanishing.
 */
const ACTION_LABELS: Record<string, string> = {
  'crew_schedule_published': 'Published the crew schedule',
  'crew-future-cleared':     'Cleared the upcoming crew schedule',
  'crew-cell-edited':        'Edited a crew day',
};

/** Date-window presets. `null` days means "everything". */
const RANGES = [
  { key: '30',  label: 'Last 30 days',  days: 30 },
  { key: '7',   label: 'Last 7 days',   days: 7 },
  { key: '90',  label: 'Last 90 days',  days: 90 },
  { key: 'all', label: 'All time',      days: null },
] as const;

type RangeKey = (typeof RANGES)[number]['key'];

function rangeStart(days: number | null): string | undefined {
  if (days === null) return undefined;
  const d = new Date();
  d.setDate(d.getDate() - days);
  // Local calendar date, so "last 30 days" means what the viewer's calendar
  // says rather than what UTC's does.
  return toLocalDateInputValue(d);
}

/** "10904 Example Site", or whichever half exists. Used by both the rows and
 *  the job filter, which carry the title under different keys.
 *  (Invented title: comments here are published — see vite.config.ts.) */
function jobLabel(jobNumber: string | null, title: string | null): string {
  if (jobNumber && title) return `${jobNumber} ${title}`;
  return jobNumber ?? title ?? '—';
}

/**
 * What an empty result means. Exported so both branches are testable: the
 * filtered one depends on component state that a static render can't reach, and
 * an assertion that can only ever see the unfiltered branch would pass whatever
 * this said.
 *
 * The distinction is the point. "Nothing matched your filters" and "nothing was
 * recorded" produce an identical empty table but mean opposite things to
 * someone chasing a change they believe happened.
 */
export function emptyMessage(filtered: boolean): string {
  return filtered
    ? 'No schedule changes match these filters in this date range.'
    : 'No schedule changes recorded in this date range.';
}

/** The filter fields that decide whether a cached page still describes the
 *  question being asked. `page` and `before` are navigation, not filtering. */
export type HistoryFilters = { from?: string; projectId?: number | ''; actorId?: string };

/**
 * Whether a previous page may stay on screen while the next one loads.
 *
 * ⚠️ Only when the FILTERS are unchanged. React Query's plain
 * `keepPreviousData` hands the old result to the new query key and reports it
 * as success, so changing Person or Job renders the previous person's rows
 * underneath the new filter until the fetch lands. On an audit view that
 * briefly attributes someone else's changes to the person just selected —
 * a wrong answer, which is worse than the empty flash it was avoiding.
 *
 * Exported because it is the whole of that guarantee, and a static render
 * cannot drive the filter state that would exercise it.
 */
export function keepAcrossPages<T>(
  prev: T | undefined,
  prevFilters: HistoryFilters | undefined,
  now: HistoryFilters,
): T | undefined {
  if (!prevFilters) return undefined;
  const same =
    prevFilters.from === now.from &&
    prevFilters.projectId === now.projectId &&
    prevFilters.actorId === now.actorId;
  return same ? prev : undefined;
}

export default function HistoryView() {
  const [range, setRange]   = useState<RangeKey>('30');
  const [projectId, setProjectId] = useState<number | ''>('');
  const [actorId, setActorId]     = useState<string>('');
  // One keyset cursor per page already visited. The stack is what makes "Newer"
  // work without an offset: going back means popping to the cursor that produced
  // the previous page, not recomputing a position in a set that may have moved.
  // An empty stack is page 0.
  const [cursors, setCursors] = useState<{ at: string; id: number }[]>([]);
  const page = cursors.length;
  const cursor = cursors[cursors.length - 1];

  const from = useMemo(
    () => rangeStart(RANGES.find((r) => r.key === range)?.days ?? 30),
    [range],
  );

  // Any filter change invalidates every cursor — they point into the old result
  // set. Without this, narrowing to one person while on page 3 shows an empty
  // table and reads as "this person changed nothing", the exact wrong answer for
  // an audit view.
  useEffect(() => { setCursors([]); }, [range, projectId, actorId]);

  const q = useQuery({
    queryKey: ['manpower', 'schedule-history', { from, projectId, actorId, page, cursor }],
    queryFn: () => manpowerApi.scheduleHistory({
      from,
      projectId: projectId === '' ? undefined : projectId,
      actorId: actorId || undefined,
      cursorAt: cursor?.at,
      cursorId: cursor?.id,
      limit: PAGE_SIZE,
    }),
    // Keeps the previous page on screen while the next one loads, but only
    // while paging within the same filters — see keepAcrossPages.
    placeholderData: (prev, prevQuery) =>
      keepAcrossPages(prev, prevQuery?.queryKey?.[2] as HistoryFilters | undefined,
        { from, projectId, actorId }),
  });

  const rows = q.data?.rows ?? [];
  const filtered = projectId !== '' || actorId !== '';

  return (
    <div>
      {/* ── Filter bar ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        <select
          aria-label="Job"
          style={SELECT}
          value={projectId}
          onChange={(e) => setProjectId(e.target.value === '' ? '' : Number(e.target.value))}
        >
          <option value="">All jobs</option>
          {(q.data?.projects ?? []).map((p) => (
            <option key={p.id} value={p.id}>{jobLabel(p.jobNumber, p.title)}</option>
          ))}
        </select>

        <select
          aria-label="Person"
          style={SELECT}
          value={actorId}
          onChange={(e) => setActorId(e.target.value)}
        >
          <option value="">Anyone</option>
          {(q.data?.actors ?? []).map((a) => (
            <option key={a.id} value={a.id}>{a.name}</option>
          ))}
        </select>

        <select
          aria-label="Date range"
          style={SELECT}
          value={range}
          onChange={(e) => setRange(e.target.value as RangeKey)}
        >
          {RANGES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
        </select>

        {filtered && (
          <button
            type="button"
            className="btn btn-secondary"
            style={{ fontSize: 12, padding: '5px 12px' }}
            onClick={() => { setProjectId(''); setActorId(''); }}
          >
            Clear filters
          </button>
        )}

        <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--ew-n-400)' }}>
          Publishes, clears and single-day edits. Not per-cell history.
        </span>
      </div>

      {/* ── States ── */}
      {q.isPending && (
        <div style={{ padding: 24, fontSize: 13, color: 'var(--ew-n-400)' }}>Loading history…</div>
      )}

      {q.isError && (
        <div style={{ padding: 16, fontSize: 13, color: 'var(--ew-red, #b8321c)' }}>
          Couldn’t load the schedule history.{' '}
          <button
            type="button"
            className="btn btn-secondary"
            style={{ fontSize: 12, padding: '4px 10px', marginLeft: 8 }}
            onClick={() => q.refetch()}
          >
            Retry
          </button>
        </div>
      )}

      {q.isSuccess && rows.length === 0 && (
        <div style={{ padding: 24, fontSize: 13, color: 'var(--ew-n-400)' }}>
          {emptyMessage(filtered)}
        </div>
      )}

      {q.isSuccess && rows.length > 0 && (
        <>
          <div style={{ overflowX: 'auto', border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-md)' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 720 }}>
              <thead>
                <tr>
                  <th style={{ ...TH, width: 190 }}>When</th>
                  <th style={{ ...TH, width: 170 }}>Who</th>
                  <th style={{ ...TH, width: 260 }}>Job</th>
                  <th style={{ ...TH, borderRight: 'none' }}>What changed</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    {/* fmtDateTime, not fmtDate: created_at is a real moment, and
                        fmtDate would render an 8pm-Eastern event as tomorrow. */}
                    <td style={{ ...TD, whiteSpace: 'nowrap', color: 'var(--ew-n-500)' }}>
                      {fmtDateTime(r.at)}
                    </td>
                    <td style={{ ...TD, fontWeight: 600 }}>
                      {/* A removed user leaves the row but not the name. Saying
                          so is better than a blank cell that reads as a bug. */}
                      {r.by ?? <span style={{ fontWeight: 400, color: 'var(--ew-n-400)' }}>Unknown user</span>}
                    </td>
                    <td style={TD}>
                      {r.projectId
                        ? <a href={`/projects/${r.projectId}`} style={{ color: 'var(--ew-green)' }}>{jobLabel(r.jobNumber, r.projectTitle)}</a>
                        : <span style={{ color: 'var(--ew-n-400)' }}>—</span>}
                    </td>
                    <td style={{ ...TD, borderRight: 'none' }}>
                      <div>{ACTION_LABELS[r.action] ?? r.action}</div>
                      {r.detail && (
                        <div style={{ fontSize: 11, color: 'var(--ew-n-400)', marginTop: 2 }}>{r.detail}</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ── Pager. Keyset, not offset, and not an accumulating "load more":
              filters change the result set underneath, and an accumulated list
              would keep rows that no longer match.

              Older pushes the page's last row as the next cursor; Newer pops.
              Both are plain click handlers rather than an effect, so there is no
              render loop to reason about. ── */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
            <button
              type="button"
              className="btn btn-secondary"
              style={{ fontSize: 12, padding: '5px 12px' }}
              disabled={page === 0 || q.isFetching}
              onClick={() => setCursors((c) => c.slice(0, -1))}
            >
              ← Newer
            </button>
            <span style={{ fontSize: 11, color: 'var(--ew-n-400)' }}>
              {/* Positions are counted from the pages walked, since a keyset
                  query has no absolute offset to report. */}
              {page * PAGE_SIZE + 1}–{page * PAGE_SIZE + rows.length}
            </span>
            <button
              type="button"
              className="btn btn-secondary"
              style={{ fontSize: 12, padding: '5px 12px' }}
              disabled={!q.data?.nextCursor || q.isFetching}
              onClick={() => {
                const next = q.data?.nextCursor;
                if (next) setCursors((c) => [...c, next]);
              }}
            >
              Older →
            </button>
          </div>
        </>
      )}
    </div>
  );
}
